import {
    Badge,
    Box,
    Button,
    Checkbox,
    CloseButton,
    Group,
    Loader,
    Modal,
    Stack,
    Switch,
    Table,
    Tabs,
    Text,
    TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
    ADMIN_SECTION_CATALOG,
    AdminCapabilityEnum,
    AdminPermissions,
    emptyAdminPermissions,
    fullAdminPermissions,
} from '@routellers/types/dist/admin/admin-permissions';
import { AdminRO } from '@routellers/types/dist/admin/admin.ro';
import { CityReadyRouteRO } from '@routellers/types/dist/city-ready-route/city-ready-route.ro';
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../api';
import { Section } from '../components/editor';
import { PageHeader } from '../components/page-header';
import { useAuth } from '../providers/auth-context';
import { AdminSectionEnum, adminCan, toggleCapability } from '../utils/admin-permissions';
import { totalFromMeta } from '../utils/list';

import { UsersActivity } from './users-activity';
import { UsersStatistics } from './users-statistics';

type EditorMode = 'create' | 'edit';

type RouteOption = { id: number; label: string };

const routeLabel = (row: CityReadyRouteRO) =>
    [row.title?.trim() || `#${row.id}`, row.cityTitle?.trim()].filter(Boolean).join(' · ');

const isTextOnlyCaps = (caps: AdminCapabilityEnum[]) =>
    caps.includes(AdminCapabilityEnum.TEXT) &&
    !caps.includes(AdminCapabilityEnum.EDIT) &&
    !caps.includes(AdminCapabilityEnum.DELETE);

type FormValues = {
    name: string;
    email: string;
    password: string;
    totalAccess: boolean;
    fieldAdmin: boolean;
    permissions: AdminPermissions;
};

const emptyForm = (): FormValues => ({
    name: '',
    email: '',
    password: '',
    totalAccess: false,
    fieldAdmin: false,
    permissions: emptyAdminPermissions(),
});

const summarizePermissions = (t: TFunction, admin: AdminRO) => {
    if (admin.totalAccess || admin.permissions == null) return t('pages.users.fullAccess');

    const sections = Object.entries(admin.permissions.sections || {}).filter(
        ([, caps]) => (caps || []).length > 0,
    );

    if (!sections.length) return t('pages.users.noRights');

    const summary = sections
        .map(
            ([section, caps]) =>
                `${t(`nav.${section}`)}: ${(caps || [])
                    .map((cap) => t(`enums.capability.${cap}`))
                    .join('+')}`,
        )
        .join(' · ');
    const routeCaps = admin.permissions?.sections?.routes || [];
    const textOnly = isTextOnlyCaps(routeCaps);

    if (!textOnly) return summary;

    return `${summary} · ${t('pages.users.assignedRoutesCount', {
        count: admin.permissions?.routeIds?.length || 0,
    })}`;
};

export const UsersPage = () => {
    const { t } = useTranslation();
    const { admin: current } = useAuth();
    const canView = adminCan(current, AdminSectionEnum.USERS, AdminCapabilityEnum.VIEW);
    const canEdit = adminCan(current, AdminSectionEnum.USERS, AdminCapabilityEnum.EDIT);
    const canDelete = adminCan(current, AdminSectionEnum.USERS, AdminCapabilityEnum.DELETE);

    const [rows, setRows] = useState<AdminRO[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [opened, setOpened] = useState(false);
    const [mode, setMode] = useState<EditorMode>('create');
    const [editingId, setEditingId] = useState<number | null>(null);
    const [routeOptions, setRouteOptions] = useState<RouteOption[]>([]);
    const [routeOptionsLoaded, setRouteOptionsLoaded] = useState(false);
    const [routeOptionsLoading, setRouteOptionsLoading] = useState(false);
    const [routeQuery, setRouteQuery] = useState('');

    const form = useForm<FormValues>({ initialValues: emptyForm() });
    const routeCaps = form.values.permissions.sections[AdminSectionEnum.ROUTES] || [];
    const assigningRoutes =
        opened && !form.values.totalAccess && isTextOnlyCaps(routeCaps);

    const load = useCallback(async () => {
        setLoading(true);

        try {
            const { data } = await api.admins.query(undefined);

            setRows(data || []);
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (canView) load();
    }, [canView, load]);

    useEffect(() => {
        if (!assigningRoutes || routeOptionsLoaded) return;

        let cancelled = false;

        const loadRoutes = async () => {
            setRouteOptionsLoading(true);

            try {
                const all: CityReadyRouteRO[] = [];
                let page = 1;

                while (page <= 30) {
                    const where: Record<string, unknown> = { id: null };
                    const { data } = await api.cityReadyRoutesApi.query({
                        page,
                        limit: 100,
                        sort: 'id',
                        order: 'ASC',
                        where,
                    });
                    const items = data?.items || [];

                    all.push(...items);

                    const total = totalFromMeta(data?.meta, items.length);

                    if (!items.length || all.length >= total) break;
                    page += 1;
                }

                if (!cancelled) {
                    setRouteOptions(all.map((row) => ({ id: row.id, label: routeLabel(row) })));
                }
            } catch (error) {
                if (!cancelled) {
                    notifications.show({ color: 'red', message: apiErrorMessage(error) });
                }
            } finally {
                if (!cancelled) {
                    setRouteOptionsLoaded(true);
                    setRouteOptionsLoading(false);
                }
            }
        };

        loadRoutes();

        return () => {
            cancelled = true;
        };
    }, [assigningRoutes, routeOptionsLoaded]);

    const openCreate = () => {
        setMode('create');
        setEditingId(null);
        form.setValues(emptyForm());
        form.resetDirty();
        setOpened(true);
    };

    const openEdit = (row: AdminRO) => {
        if (!canEdit) return;

        setMode('edit');
        setEditingId(row.id);
        form.setValues({
            name: row.name || '',
            email: row.email || '',
            password: '',
            totalAccess: Boolean(row.totalAccess),
            fieldAdmin: Boolean(row.fieldAdmin),
            permissions:
                row.permissions == null
                    ? fullAdminPermissions()
                    : {
                          sections: { ...(row.permissions.sections || {}) },
                          routeIds: Array.isArray(row.permissions.routeIds)
                              ? [...row.permissions.routeIds]
                              : null,
                      },
        });
        form.resetDirty();
        setOpened(true);
    };

    const save = async () => {
        if (!form.values.name.trim() || !form.values.email.trim()) {
            notifications.show({ color: 'red', message: t('pages.users.nameEmailRequired') });

            return;
        }

        if (mode === 'create' && !form.values.password.trim()) {
            notifications.show({ color: 'red', message: t('pages.users.passwordRequired') });

            return;
        }

        setSaving(true);

        try {
            const permissions = form.values.totalAccess
                ? fullAdminPermissions()
                : {
                      sections: form.values.permissions.sections,
                      routeIds: assigningRoutes
                          ? form.values.permissions.routeIds || []
                          : null,
                  };

            if (mode === 'create') {
                await api.admins.create({
                    data: {
                        name: form.values.name.trim(),
                        email: form.values.email.trim(),
                        password: form.values.password,
                        phone: '',
                        totalAccess: form.values.totalAccess,
                        fieldAdmin: form.values.fieldAdmin,
                        permissions,
                    },
                });
                notifications.show({ color: 'green', message: t('pages.users.adminCreated') });
            } else if (editingId != null) {
                await api.admins.update({
                    id: editingId,
                    data: {
                        name: form.values.name.trim(),
                        email: form.values.email.trim(),
                        phone: '',
                        totalAccess: form.values.totalAccess,
                        fieldAdmin: form.values.fieldAdmin,
                        permissions,
                        ...(form.values.password.trim() ? { password: form.values.password } : {}),
                    },
                });
                notifications.show({ color: 'green', message: t('pages.users.adminUpdated') });
            }

            setOpened(false);
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        } finally {
            setSaving(false);
        }
    };

    const remove = async (row: AdminRO) => {
        if (!canDelete) return;
        if (row.id === current?.id) {
            notifications.show({ color: 'red', message: t('pages.users.cannotDeleteSelf') });

            return;
        }

        if (!window.confirm(t('pages.users.confirmDelete', { email: row.email }))) return;

        try {
            await api.admins.remove(row.id);
            notifications.show({ color: 'green', message: t('pages.users.adminDeleted') });
            await load();
        } catch (error) {
            notifications.show({ color: 'red', message: apiErrorMessage(error) });
        }
    };

    const permissionMatrix = useMemo(() => ADMIN_SECTION_CATALOG, []);

    if (!canView) {
        return (
            <Stack gap='md'>
                <PageHeader title={t('pages.users.title')} subtitle={t('pages.users.noAccess')} />
            </Stack>
        );
    }

    const listPanel = (
        <>
            {loading ? (
                <Loader />
            ) : (
                <Table.ScrollContainer minWidth={720}>
                    <Table striped highlightOnHover withTableBorder>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>{t('pages.users.columns.name')}</Table.Th>
                                <Table.Th>{t('pages.users.columns.email')}</Table.Th>
                                <Table.Th>{t('pages.users.columns.access')}</Table.Th>
                                <Table.Th w={140} />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {rows.map((row) => (
                                <Table.Tr key={row.id}>
                                    <Table.Td>
                                        <Text size='sm' fw={600}>
                                            {row.name}
                                        </Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Text size='sm'>{row.email}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Stack gap={4}>
                                            {row.totalAccess || row.permissions == null ? (
                                                <Badge color='green' variant='light'>
                                                    {t('pages.users.fullAccess')}
                                                </Badge>
                                            ) : (
                                                <Text size='xs' c='dimmed' lineClamp={2}>
                                                    {summarizePermissions(t, row)}
                                                </Text>
                                            )}
                                            {row.fieldAdmin || row.totalAccess ? (
                                                <Badge color='blue' variant='light'>
                                                    {t('pages.users.fieldAdmin')}
                                                </Badge>
                                            ) : null}
                                        </Stack>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap='xs' justify='flex-end'>
                                            {canEdit && (
                                                <Button
                                                    size='xs'
                                                    variant='light'
                                                    onClick={() => openEdit(row)}
                                                >
                                                    {t('pages.users.edit')}
                                                </Button>
                                            )}
                                            {canDelete && (
                                                <Button
                                                    size='xs'
                                                    variant='subtle'
                                                    color='red'
                                                    onClick={() => remove(row)}
                                                >
                                                    {t('pages.users.delete')}
                                                </Button>
                                            )}
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            ))}
                            {!rows.length && (
                                <Table.Tr>
                                    <Table.Td colSpan={4}>
                                        <Text size='sm' c='dimmed'>
                                            {t('pages.users.empty')}
                                        </Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                        </Table.Tbody>
                    </Table>
                </Table.ScrollContainer>
            )}

            <Modal
                opened={opened}
                onClose={() => setOpened(false)}
                title={
                    mode === 'create'
                        ? t('pages.users.modal.createTitle')
                        : t('pages.users.modal.editTitle')
                }
                size='lg'
                centered
            >
                <Stack gap='lg'>
                    <Section boxed title={t('pages.users.modal.account')}>
                        <TextInput
                            label={t('pages.users.modal.name')}
                            required
                            {...form.getInputProps('name')}
                        />
                        <TextInput
                            label={t('pages.users.modal.email')}
                            required
                            type='email'
                            {...form.getInputProps('email')}
                        />
                        <TextInput
                            label={
                                mode === 'create'
                                    ? t('pages.users.modal.password')
                                    : t('pages.users.modal.newPassword')
                            }
                            required={mode === 'create'}
                            type='password'
                            description={
                                mode === 'edit' ? t('pages.users.modal.passwordHint') : undefined
                            }
                            {...form.getInputProps('password')}
                        />
                        <Switch
                            label={t('pages.users.modal.fullAccessSwitch')}
                            checked={form.values.totalAccess}
                            onChange={(e) =>
                                form.setFieldValue('totalAccess', e.currentTarget.checked)
                            }
                        />
                        <Switch
                            label={t('pages.users.modal.fieldAdminSwitch')}
                            description={
                                form.values.totalAccess
                                    ? t('pages.users.modal.fieldAdminFromFullAccess')
                                    : t('pages.users.modal.fieldAdminHint')
                            }
                            checked={form.values.fieldAdmin || form.values.totalAccess}
                            disabled={form.values.totalAccess}
                            onChange={(e) =>
                                form.setFieldValue('fieldAdmin', e.currentTarget.checked)
                            }
                        />
                    </Section>

                    {!form.values.totalAccess && (
                        <Section
                            boxed
                            title={t('pages.users.modal.permissions')}
                            description={t('pages.users.modal.permissionsHint')}
                        >
                            <Stack gap='md'>
                                {permissionMatrix.map((row) => {
                                    const caps =
                                        form.values.permissions.sections[row.section] || [];

                                    return (
                                        <Box
                                            key={row.section}
                                            p='sm'
                                            style={{
                                                border: '1px solid var(--mantine-color-gray-3)',
                                                borderRadius: 'var(--mantine-radius-sm)',
                                            }}
                                        >
                                            <Text size='sm' fw={600}>
                                                {t(`nav.${row.section}`)}
                                            </Text>
                                            <Text size='xs' c='dimmed' mb='xs'>
                                                {t(
                                                    `pages.users.sectionDescriptions.${row.section}`,
                                                )}
                                            </Text>
                                            <Group gap='md'>
                                                {row.capabilities.map((capability) => (
                                                    <Checkbox
                                                        key={capability}
                                                        label={t(`enums.capability.${capability}`)}
                                                        checked={caps.includes(capability)}
                                                        onChange={(e) => {
                                                            let next = toggleCapability(
                                                                form.values.permissions,
                                                                row.section,
                                                                capability,
                                                                e.currentTarget.checked,
                                                            );

                                                            if (row.section === AdminSectionEnum.ROUTES) {
                                                                const nextCaps =
                                                                    next.sections[
                                                                        AdminSectionEnum.ROUTES
                                                                    ] || [];

                                                                next = {
                                                                    ...next,
                                                                    routeIds: isTextOnlyCaps(nextCaps)
                                                                        ? (next.routeIds ?? [])
                                                                        : null,
                                                                };
                                                            }

                                                            form.setFieldValue('permissions', next);
                                                        }}
                                                    />
                                                ))}
                                            </Group>
                                            {row.section === AdminSectionEnum.ROUTES &&
                                                assigningRoutes && (
                                                    <Stack gap='xs' mt='sm'>
                                                        <TextInput
                                                            label={t(
                                                                'pages.users.modal.assignedRoutes',
                                                            )}
                                                            description={t(
                                                                'pages.users.modal.assignedRoutesHint',
                                                            )}
                                                            placeholder={t(
                                                                'pages.users.modal.routeSearch',
                                                            )}
                                                            value={routeQuery}
                                                            onChange={(e) =>
                                                                setRouteQuery(e.currentTarget.value)
                                                            }
                                                        />
                                                        {!(form.values.permissions.routeIds || [])
                                                            .length && (
                                                            <Text size='xs' c='orange'>
                                                                {t(
                                                                    'pages.users.modal.noRoutesSelected',
                                                                )}
                                                            </Text>
                                                        )}
                                                        <Group gap='xs'>
                                                            {(
                                                                form.values.permissions.routeIds ||
                                                                []
                                                            ).map((id) => (
                                                                <Badge
                                                                    key={id}
                                                                    variant='light'
                                                                    rightSection={
                                                                        <CloseButton
                                                                            size='xs'
                                                                            onMouseDown={(e) =>
                                                                                e.preventDefault()
                                                                            }
                                                                            onClick={() =>
                                                                                form.setFieldValue(
                                                                                    'permissions',
                                                                                    {
                                                                                        ...form
                                                                                            .values
                                                                                            .permissions,
                                                                                        routeIds: (
                                                                                            form
                                                                                                .values
                                                                                                .permissions
                                                                                                .routeIds ||
                                                                                            []
                                                                                        ).filter(
                                                                                            (
                                                                                                routeId,
                                                                                            ) =>
                                                                                                routeId !==
                                                                                                id,
                                                                                        ),
                                                                                    },
                                                                                )
                                                                            }
                                                                        />
                                                                    }
                                                                >
                                                                    {routeOptions.find(
                                                                        (route) => route.id === id,
                                                                    )?.label || `#${id}`}
                                                                </Badge>
                                                            ))}
                                                        </Group>
                                                        {routeOptionsLoading && (
                                                            <Text size='xs' c='dimmed'>
                                                                {t(
                                                                    'pages.users.modal.routesLoading',
                                                                )}
                                                            </Text>
                                                        )}
                                                        <Stack gap={4}>
                                                            {routeOptions
                                                                .filter((route) => {
                                                                    const query = routeQuery
                                                                        .trim()
                                                                        .toLowerCase();

                                                                    if (!query) return false;
                                                                    if (
                                                                        (
                                                                            form.values.permissions
                                                                                .routeIds || []
                                                                        ).includes(route.id)
                                                                    ) {
                                                                        return false;
                                                                    }

                                                                    return route.label
                                                                        .toLowerCase()
                                                                        .includes(query);
                                                                })
                                                                .slice(0, 8)
                                                                .map((route) => (
                                                                    <Button
                                                                        key={route.id}
                                                                        variant='subtle'
                                                                        size='compact-sm'
                                                                        justify='flex-start'
                                                                        onClick={() => {
                                                                            const current =
                                                                                form.values
                                                                                    .permissions
                                                                                    .routeIds || [];

                                                                            if (
                                                                                current.includes(
                                                                                    route.id,
                                                                                )
                                                                            ) {
                                                                                return;
                                                                            }

                                                                            form.setFieldValue(
                                                                                'permissions',
                                                                                {
                                                                                    ...form.values
                                                                                        .permissions,
                                                                                    routeIds: [
                                                                                        ...current,
                                                                                        route.id,
                                                                                    ],
                                                                                },
                                                                            );
                                                                            setRouteQuery('');
                                                                        }}
                                                                    >
                                                                        {route.label}
                                                                    </Button>
                                                                ))}
                                                        </Stack>
                                                    </Stack>
                                                )}
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </Section>
                    )}

                    <Group justify='flex-end'>
                        <Button variant='default' onClick={() => setOpened(false)}>
                            {t('pages.users.modal.cancel')}
                        </Button>
                        <Button loading={saving} onClick={save}>
                            {mode === 'create'
                                ? t('pages.users.modal.create')
                                : t('pages.users.modal.save')}
                        </Button>
                    </Group>
                </Stack>
            </Modal>
        </>
    );

    return (
        <Stack gap='md'>
            <PageHeader
                title={t('pages.users.title')}
                action={
                    canEdit ? (
                        <Button onClick={openCreate}>{t('pages.users.createAdmin')}</Button>
                    ) : undefined
                }
            />

            {current?.totalAccess ? (
                <Tabs defaultValue='admins'>
                    <Tabs.List mb='md'>
                        <Tabs.Tab value='admins'>{t('pages.users.tabs.admins')}</Tabs.Tab>
                        <Tabs.Tab value='activity'>{t('pages.users.tabs.activity')}</Tabs.Tab>
                        <Tabs.Tab value='statistics'>{t('pages.users.tabs.statistics')}</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value='admins'>{listPanel}</Tabs.Panel>
                    <Tabs.Panel value='activity'>
                        <UsersActivity />
                    </Tabs.Panel>
                    <Tabs.Panel value='statistics'>
                        <UsersStatistics />
                    </Tabs.Panel>
                </Tabs>
            ) : (
                listPanel
            )}
        </Stack>
    );
};
